import { useQuery } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { ConversationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * How often the inbox re-reads the conversation list so unread_count and the
 * last_message preview stay fresh without a manual refresh (mirrors
 * SESSION_REFETCH_MS in modules/dropin/hooks/useDropInSession.ts). The thread
 * view (a later slice) reads once and does not poll.
 */
const CONVERSATIONS_REFETCH_MS = 30_000;

/**
 * The caller's DM inbox (GET /social/conversations → ConversationResponse[]),
 * returned VERBATIM as the server's ENRICHED objects — each carries its
 * participants (display_name + avatar_url, resolved server-side), the
 * last_message preview, the unread_count, and last_message_at. The backend scopes
 * the list to the token user, so the cache key carries no uid.
 *
 * THIN presentation: the page renders these fields as-is. The ONLY view-side
 * computation is picking the counterpart (the participant whose user_id !== me) to
 * label a row — never a derived unread count, never a client re-sort. Polled on an
 * interval so the unread badge + preview track the server's truth.
 */
export function useConversations() {
  return useQuery<ConversationResponse[]>({
    queryKey: queryKeys.social.conversations(),
    queryFn: () => socialApi.listConversations(),
    refetchInterval: CONVERSATIONS_REFETCH_MS,
  });
}
